<!--
 * Path: packages/frontend/src/features/browser/forms/mindmap/Mindmap.vue
 * Task: Wrapper for mindmap view using FormBlockWrapper.
 * Update: Reconfigured dynamic schema parsing to support multitype source/target dropdowns.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    title="Connected Artifacts"
    info="Visualization of predecessor and successor relationships."
    :can-edit="canEdit"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="emitInfo"
  >
    <template #read>
      <MindmapRead :hide-header="true" @show-info="emitInfo" />
    </template>
    <template #edit>
      <Multiselect
        :field="predecessorField"
        @show-info="handleShowInfo(predecessorField)"
      />
      <Multiselect
        :field="successorField"
        @show-info="handleShowInfo(successorField)"
        class="mb-0"
      />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useAuthStore } from '@/store/auth';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import MindmapRead from './MindmapRead.vue';
import Multiselect from '@/core/components/forms/Multiselect.vue';

const props = defineProps<{ field?: any }>(); // Added to prevent Vue from complaining when component is dynamically injected

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const authStore = useAuthStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const emit = defineEmits<{
  (e: 'show-info', payload: { title?: string; text?: string }): void;
}>();

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value);

// Retrieve schema directly from the global app settings
const formSchema = computed<any[]>(() => {
  const entityDef = authStore.appData?.entities?.find((e: any) => e.name === (formContext?.entity.value?.entityType || workspaceStore.selectedEntity?.entityType));
  return entityDef?.fields || [];
});

const predecessorField = computed(() => {
  const schemaDef = formSchema.value.find(f => f.id === 'predecessorIds');
  return schemaDef || {
    id: 'predecessorIds', name: 'Predecessors', entitySource: 'predecessors'
  };
});

const successorField = computed(() => {
  const schemaDef = formSchema.value.find(f => f.id === 'successorIds');
  return schemaDef || {
    id: 'successorIds', name: 'Successors', entitySource: 'predecessors'
  };
});

const emitInfo = () => emit('show-info', { title: 'Connected Artifacts', text: 'Visualization of predecessor and successor relationships.' });

const handleShowInfo = (field: any) => {
  emit('show-info', { title: field.name, text: field.info });
};

// --- Block-Level Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};
</script>
